import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { CommonService } from 'src/app/common/service/common.service';
import { OrganizationInfo } from 'src/app/user/organization-management/models/organization.model';
import { HttpClientService } from 'src/app/service/http-client.service';
import { isSuccess, Response } from 'src/app/service/response';
import { UtilsService } from 'src/app/service/utils.service';
import { EntranceApproveInfo, EntranceInfo, UserInfo } from 'src/app/user/user-management/models/user.model';
import { PermissionInfo } from 'src/app/access/access-permission/models/permission-info.model';

export const APPROVE = 'approve';
export const REJECT = 'reject';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private httpClient: HttpClientService,
    private common: CommonService,
    private utils: UtilsService
  ) {

  }

  searchUser(name: string) {
    const url = '/searchUser';
    const data = {
      name,
    };
    return this.post(url, data);
  }

  /**
   * 사용자 목록. 트리에서 조직을 고를 때마다 호출된다.
   *
   * showLoading=false 인 이유: 응답이 보통 수십 ms 인데 왕복이 스피너 지연(300ms)
   * 경계에 걸리면 떴다 바로 닫혀 화면이 번쩍인다. 사용자가 버튼을 눌러 기다리는
   * 조회가 아니라 선택에 따라오는 갱신이라, 스피너 없이 조용히 바뀌는 편이 낫다.
   */
  getUserList(orgId: string, name?: string, id?: string, page = 1, pageSize = 10) {
    const url = '/searchUserList';
    const data: any = { orgId, page, pageSize };
    if (name) data.name = name;
    if (id) data.id = id;
    return this.post(url, data, false);
  }

  getUserDetailInfo(id: string) {
    const url = '/searchUserDetailInfo';
    const data = {
      id
    };
    return this.post(url, data);
  }

  getUserPermissionList(id: string) {
    const url = '/searchUserEntranceGroupList';
    const data = {
      id
    };
    return this.post(url, data);
  }

  addUserPermissions(updaterId: string, id: string, permissions: PermissionInfo[]) {
    const url = '/addUserEntranceGroupList';
    const data = {
      updaterId,
      userInfo: {
        id,
        entranceGroupInfoList: permissions
      }
    };
    return this.post(url, data);
  }

  deleteUserPermission(updaterId: string, id: string, permissions: PermissionInfo[]) {
    const url = '/deleteUserEntranceGroup';
    const data = {
      updaterId,
      userInfo: {
        id,
        entranceGroupInfoList: permissions
      }
    };
    return this.post(url, data);
  }

  changeUserOrganization(updaterId: string, userId: string, orgInfo: OrganizationInfo) {
    const url = '/changeUserOrgInfo';
    const data = {
      updaterId,
      userInfo: {
        id: userId,
        orgInfo
      }
    };
    return this.post(url, data);
  }

  approvePermission(updaterId: string, userInfo: UserInfo) {
    const url = '/approveUserEntranceByAdmin';
    const data = {
      updaterId,
      userInfo
    };
    return this.post(url, data);
  }

  rejectPermission(updaterId: string, userInfo: UserInfo) {
    const url = '/rejectUserEntranceByAdmin';
    const data = {
      updaterId,
      userInfo
    };
    return this.post(url, data);
  }

  initUserPassword(updaterId: string, userInfo: UserInfo) {
    const url = '/initPassword';
    const data = {
      updaterId,
      loginInfo: {
        id: userInfo.id
      }
    };
    return this.post(url, data);
  }

  applyEntranceGroupsToUsers(updaterId: string, userIds: string[], entranceGroupIds: string[]) {
    const url = '/applyEntranceGroupsToUsers';
    const data = { userIds, entranceGroupIds, updaterId };
    return this.post(url, data);
  }

  deleteUser(updaterId: string, userId: string) {
    const url = '/deleteUser';
    const data = {
      updaterId,
      userInfo: {
        id: userId
      }
    };
    return this.post(url, data);
  }



  updateUserAccessDuration(updaterId: string, userId: string, approveInfo: any) {
    const url = '/changeUserEntranceApproveInfo';
    const data = {
      updaterId,
      userInfo: {
        id: userId,
        requestEntranceApproveInfo: approveInfo
      }
    };
    return this.post(url, data);
  }

  downloadUserInfoFile(updaterId: string, orgId: string, downloadPurpose: string) {
    const url = '/downloadUserList';
    const data = {
      updaterId,
      downloadPurpose,
      searchFormInfo: {
        orgId
      },
    };
    return this.common.downloadFile(url, data);
  }

  post(url: string, data: any, showLoading: boolean = true) {
    return this.httpClient.post(url, data, showLoading).pipe(tap((res: Response) => {
      if (!isSuccess(res.resultCode)) {
        this.utils.openMessageDialog('사용자요청 실패', res.resultMsg);
      }
    }));
  }

}
